import React, { useState, useEffect, ReactNode, Dispatch, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import { useCalculateElapsedTime } from '../../hooks/notifications/use-calculate-elapsed-time';
import { useNotificationsOperations } from '../../hooks/notifications/use-notifications-operations';
import { ROUTES } from '../../routes/routes';
import { NotificationType, NotificationInvitationType, NotificationTypeEnum } from '../../types';
import { CircularProgress } from '../circular-progress';
import { IconDelete, IconNotification } from '../icons';
import './index.scss';

type NotificationItemProps = {
  notification: NotificationType;
  title: string;
  setNotifications: Dispatch<SetStateAction<NotificationType[]>>;
  children: ReactNode;
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  title,
  children,
  setNotifications,
}) => {
  const navigate = useNavigate();
  const { markAsHidden, markAsDisplayed, markAsHiddenLoading, markAsDisplayedLoading } = useNotificationsOperations();
  const { timeElepse } = useCalculateElapsedTime(notification.createdTimestampUTC);
  const [isSwiped, setSwiped] = useState<boolean>(false);

  const onDeleteNotification = () =>
    markAsHidden(notification.id, {
      onSuccess: () => {
        setNotifications((prev) => {
          const tempList = cloneDeep(prev);
          const index = tempList.findIndex((item) => item.id === notification.id);
          if (index !== -1) tempList[index].isHidden = true;

          return tempList;
        });
      },
    });

  const handlers = useSwipeable({
    onSwipedLeft: () => setSwiped(false),
    onSwipedRight: () => setSwiped(true),
    trackMouse: true,
    preventDefaultTouchmoveEvent: true,
  });

  const onClick = () => {
    const { data } = notification;

    if (
      data.type === NotificationTypeEnum.INVITATION &&
      [NotificationInvitationType.InvitationReceived, NotificationInvitationType.InvitationAcceptedByInvitee].includes(
        data.invitationType,
      )
    ) {
      navigate(ROUTES.NotificationDetails(notification.id));
    } else {
      markAsDisplayed(notification.id, {
        onSuccess: () => {
          setNotifications((prev) => {
            const tempList = cloneDeep(prev);
            const index = tempList.findIndex((item) => item.id === notification.id);
            if (index !== -1) tempList[index].wasDisplayed = true;

            return tempList;
          });
        },
      });
    }
  };


  const loading = markAsHiddenLoading || markAsDisplayedLoading;

  return (
    <div className="notification-item-wrapper">
      <div className="notification-item-wrapper__delete-section" onClick={onDeleteNotification}>
        <IconDelete className="icon-delete--element" />
      </div>
      <div
        className={classNames('notification-item box box--rounder', {
          'notification-item--was-displayed': notification.wasDisplayed,
          'notification-item--is-swiped': isSwiped,
        })}
        onClick={onClick}
        {...handlers}
      >
        <header className="notification-item__header">
          <h4 className="notification-item__header-title">{title}</h4>
          <div className="notification-item__header-icon">
            {loading ? <CircularProgress size={20} /> : <IconNotification isOn={!notification.wasDisplayed} />}
          </div>
        </header>
        {children}
        {notification.createdTimestampUTC && <p className="notification-item__time-elapse">{timeElepse}</p>}
      </div>
    </div>
  );
};
